import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {catchError, map, take} from 'rxjs/operators';
import {AccountConfig} from '@app/interfaces/config/account.config';
import {HttpClient} from '@angular/common/http';
import {environment} from '@src/environments/environment';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {

  constructor(private configApi: ConfigAPIService, private http: HttpClient) {
  }

  getFeatureFlags(accountUuid: string = null): Observable<AccountConfig['accountFeatureFlags']> {
    return this.configApi.getAccountConfig(accountUuid).pipe(map((accountConfig) => accountConfig.accountFeatureFlags));
  }

  /**
   * Whether a module-package feature is locked for the current account.
   *
   * Generic across any module package that gates a feature flag: locked ONLY
   * when the flag is off AND the module providing it has no free units left.
   * During a free trial (flag off, but included units remaining) it stays
   * UNLOCKED — mirroring the backend quota gate. Modules without a free-unit
   * counter simply lock on the flag alone (quota resolves to null).
   */
  isFeatureLocked(featureFlag: AccountFeaturesEnum, accountUuid: string = null): Observable<boolean> {
    return forkJoin({
      flags: this.getFeatureFlags(accountUuid).pipe(take(1)),
      quota: this.http
        .get<{ data: { canCreate?: boolean } | null }>(
          `${environment.apiUrl}/api/module-packages/quota/by-feature/${featureFlag}`
        )
        .pipe(
          take(1),
          map((res) => res?.data ?? null),
          catchError(() => of(null))
        )
    }).pipe(
      map(({flags, quota}) => {
        const flagEnabled = !!flags?.[featureFlag];
        const canCreateFree = !!quota?.canCreate;
        return !flagEnabled && !canCreateFree;
      })
    );
  }

  getCountryAvailableFeatures(accountUuid: string = null): Observable<AccountConfig['countryAvailableFeatures']> {
    return this.configApi.getAccountConfig(accountUuid).pipe(map((accountConfig) => accountConfig.countryAvailableFeatures ?? {}));
  }
}
